import { useState, useEffect } from 'react';
import { GlassCard, GlassCardHeader, GlassCardTitle, GlassCardContent } from '@easyflow/ui-master/components/ui/glass-card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import apiClient from '@/lib/api';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  X,
  RefreshCw
} from 'lucide-react';

type AlertSeverity = 'critical' | 'warning' | 'info';

interface SystemAlert {
  id: string;
  severity: AlertSeverity;
  title: string;
  message: string;
  timestamp: Date;
  source: string;
  dismissed: boolean;
}

interface FreeSwitchStatusData {
  connected: boolean;
  message: string;
}

interface ESLHealthData {
  state: string;
  reconnectAttempts: number;
}

export function AlertsWidget() {
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void checkForAlerts();
    const interval = setInterval(checkForAlerts, 60000);
    return () => clearInterval(interval);
  }, []);

  const checkForAlerts = async () => {
    setRefreshing(true);
    const newAlerts: SystemAlert[] = [];

    try {
      const fsResponse = await apiClient.get<FreeSwitchStatusData>('/system/freeswitch/status');
      if (fsResponse.success && fsResponse.data && !fsResponse.data.connected) {
        newAlerts.push({
          id: 'fs-disconnected',
          severity: 'critical',
          title: 'FreeSWITCH Offline',
          message: 'Il servizio FreeSWITCH non risponde',
          timestamp: new Date(),
          source: 'FreeSWITCH',
          dismissed: false
        });
      }
    } catch {
      newAlerts.push({
        id: 'fs-error',
        severity: 'warning',
        title: 'FreeSWITCH Check Failed',
        message: 'Impossibile verificare lo stato di FreeSWITCH',
        timestamp: new Date(),
        source: 'FreeSWITCH',
        dismissed: false
      });
    }

    try {
      const eslResponse = await apiClient.get<ESLHealthData>('/system/esl-health');
      if (eslResponse.success && eslResponse.data) {
        if (eslResponse.data.state !== 'connected') {
          newAlerts.push({
            id: 'esl-disconnected',
            severity: 'critical',
            title: 'ESL Connection Lost',
            message: `Stato: ${eslResponse.data.state}, tentativi: ${eslResponse.data.reconnectAttempts}`,
            timestamp: new Date(),
            source: 'ESL',
            dismissed: false
          });
        }
      }
    } catch {
      // ESL check failed silently
    }

    if (newAlerts.length === 0) {
      newAlerts.push({
        id: 'all-ok',
        severity: 'info',
        title: 'Sistema Operativo',
        message: 'Tutti i servizi funzionano correttamente',
        timestamp: new Date(),
        source: 'System',
        dismissed: false
      });
    }

    setAlerts(newAlerts);
    setLoading(false);
    setRefreshing(false);
  };

  const dismissAlert = (alertId: string) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  const getSeverityIcon = (severity: AlertSeverity) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="h-4 w-4 text-rose-400" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-amber-400" />;
      case 'info':
        return <CheckCircle2 className="h-4 w-4 text-emerald-400" />;
      default:
        return <Info className="h-4 w-4 text-cyan-400" />;
    }
  };

  const getSeverityBadge = (severity: AlertSeverity) => {
    switch (severity) {
      case 'critical':
        return <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30">Critical</Badge>;
      case 'warning':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Warning</Badge>;
      case 'info':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">OK</Badge>;
      default:
        return null;
    }
  };

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  if (loading) {
    return (
      <GlassCard variant="elevated" className="h-full">
        <GlassCardHeader className="p-4 pb-2">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-amber-400" />
            <GlassCardTitle className="text-base">System Alerts</GlassCardTitle>
          </div>
        </GlassCardHeader>
        <GlassCardContent className="p-4 pt-2">
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="h-16 bg-white/5 rounded-lg animate-pulse" />
            ))}
          </div>
        </GlassCardContent>
      </GlassCard>
    );
  }

  return (
    <GlassCard variant="elevated" className="h-full">
      <GlassCardHeader className="p-4 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className={`h-5 w-5 ${criticalCount > 0 ? 'text-rose-400' : warningCount > 0 ? 'text-amber-400' : 'text-emerald-400'}`} />
            <GlassCardTitle className="text-base">System Alerts</GlassCardTitle>
            {criticalCount > 0 && (
              <Badge className="bg-rose-500/20 text-rose-400 text-xs">{criticalCount}</Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={checkForAlerts}
            disabled={refreshing}
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground dark:text-muted-foreground/70 dark:hover:text-white"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </GlassCardHeader>
      <GlassCardContent className="p-4 pt-2">
        <div className="space-y-2 max-h-[280px] overflow-y-auto">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={`flex items-start justify-between p-3 rounded-lg border transition-colors ${
                alert.severity === 'critical' 
                  ? 'bg-rose-500/10 border-rose-500/20' 
                  : alert.severity === 'warning'
                  ? 'bg-amber-500/10 border-amber-500/20'
                  : 'bg-emerald-500/10 border-emerald-500/20'
              }`}
            >
              <div className="flex items-start gap-3">
                {getSeverityIcon(alert.severity)}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-foreground text-sm">{alert.title}</span>
                    {getSeverityBadge(alert.severity)}
                  </div>
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">{alert.message}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground dark:text-muted-foreground">{alert.source}</span>
                    <span className="text-xs text-muted-foreground/70 dark:text-muted-foreground">
                      {alert.timestamp.toLocaleTimeString('it-IT')}
                    </span>
                  </div>
                </div>
              </div>
              {alert.severity !== 'info' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => dismissAlert(alert.id)}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground dark:text-muted-foreground dark:hover:text-white"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {alerts.length === 0 && (
          <div className="text-center py-8">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-400 opacity-50" />
            <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Nessun alert attivo</p>
          </div>
        )}
      </GlassCardContent>
    </GlassCard>
  );
}
